import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Loader2, MessageCircle, Search, Shield,
  Trash2, UserPlus, Users as UsersIcon, X,
} from "lucide-react";
import { AppLayout } from "../components/app/AppLayout";
import { usersApi, webUsersApi, auth } from "../lib/api";
import { TID } from "../constants/testIds";
import { getApiError } from "../lib/utils";
import UserExpandedPanel from "../components/app/UserExpandedPanel";
import MergeUserModal from "../components/app/MergeUserModal";
import OneShotPasswordModal from "../components/app/OneShotPasswordModal";
import RolesManagerModal from "../components/app/RolesManagerModal";
import EditTelegramPermissionsModal from "../components/app/EditTelegramPermissionsModalStandalone";

// ==========================================================================
// Unified Users page
// One row per person. Expand to see & edit BOTH web (email/role/password)
// AND Telegram (id/username/permissions) surfaces on the same panel.
// ==========================================================================

const RoleBadge = ({ role }) => {
  const styles = {
    SUPER_ADMIN: "border-nmi-800 bg-nmi-800 text-white",
    ADMIN: "border-nmi-700 bg-white text-nmi-700",
    VIEWER: "border-slate-300 bg-white text-slate-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${styles[role] || "border-slate-300 bg-slate-50 text-slate-700"}`}>
      {role === "SUPER_ADMIN" && <Shield size={10} />}
      {(role || "VIEWER").replace("_", " ")}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
    PENDING: "bg-amber-100 text-amber-800 border-amber-200",
    REVOKED: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${map[status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {status}
    </span>
  );
};

// A tiny badge showing which surfaces the user has: W (web) T (telegram)
const SurfaceChips = ({ user }) => {
  const hasWeb = !!user.email;
  const hasTg = !!(user.telegram_id || user.telegram_username);
  return (
    <div className="flex gap-1">
      <span
        title={hasWeb ? "Has web login" : "No web login"}
        className={`inline-flex items-center gap-0.5 rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase ${
          hasWeb ? "border-nmi-600 bg-nmi-50 text-nmi-800" : "border-slate-200 bg-slate-50 text-slate-400"
        }`}
      >
        <UsersIcon size={9} strokeWidth={2.5} /> Web
      </span>
      <span
        title={hasTg ? "Bound on Telegram" : "Not on Telegram"}
        className={`inline-flex items-center gap-0.5 rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase ${
          hasTg ? "border-blue-600 bg-blue-50 text-blue-800" : "border-slate-200 bg-slate-50 text-slate-400"
        }`}
      >
        <MessageCircle size={9} strokeWidth={2.5} /> TG
      </span>
    </div>
  );
};

// ---------- Unified Add User modal ----------
// Routes:
//  * Email provided → POST /api/web-users  (creates web login + optional TG binding).
//    Returns initial_password → shown once via OneShotPasswordModal.
//  * No email       → POST /api/users      (Telegram-only / shadow user).
const AddUserModal = ({ roles, onClose, onCreated }) => {
  const [form, setForm] = useState({
    display_name: "",
    email: "",
    role: "VIEWER",
    password: "",
    telegram_id: "",
    telegram_username: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.display_name.trim()) {
      toast.error("Display name is required");
      return;
    }
    const hasEmail = !!form.email.trim();
    const hasTgId = !!form.telegram_id.trim();
    const hasTgUsername = !!form.telegram_username.trim();
    if (!hasEmail && !hasTgId && !hasTgUsername) {
      toast.error("Provide at least one of: email, Telegram ID, or @username");
      return;
    }
    if (form.password && form.password.length < 8) {
      toast.error("Password must be 8+ characters (or leave blank for default)");
      return;
    }
    let tid = null;
    if (hasTgId) {
      tid = parseInt(form.telegram_id.trim(), 10);
      if (Number.isNaN(tid)) { toast.error("Telegram ID must be numeric"); return; }
    }
    const tgUname = hasTgUsername ? form.telegram_username.trim().replace(/^@/, "") : null;

    setSaving(true);
    try {
      if (hasEmail) {
        const payload = {
          email: form.email.trim(),
          display_name: form.display_name.trim(),
          role: form.role,
          password: form.password || null,
          telegram_id: tid,
          telegram_username: tgUname,
        };
        const res = await webUsersApi.create(payload);
        onCreated({ user: res, initial_password: res.initial_password });
      } else {
        const payload = { display_name: form.display_name.trim() };
        if (tid) payload.telegram_id = tid;
        if (tgUname) payload.telegram_username = tgUname;
        const res = await usersApi.create(payload);
        onCreated({ user: res, initial_password: null });
      }
    } catch (err) {
      toast.error(getApiError(err, "Create failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg rounded-md border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">Add user</div>
            <h3 className="font-heading text-lg font-bold text-slate-900">New account</h3>
            <p className="mt-0.5 text-[11px] text-slate-500">
              Provision either surface (or both). Users with an email sign in on the web; those with a Telegram binding can use the bot.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          <Field label="Display name" required>
            <input
              data-testid={TID.addUserName}
              value={form.display_name}
              onChange={set("display_name")}
              placeholder="e.g. Jane Doe"
              className={inputCls}
              required
            />
          </Field>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Web login (optional)</div>
            <Field label="Email">
              <input
                data-testid={TID.addUserEmail}
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="user@nationwidemh.com"
                className={inputCls}
              />
            </Field>
            {form.email.trim() && (
              <>
                <Field label="Role">
                  <select
                    data-testid={TID.addUserRole}
                    value={form.role}
                    onChange={set("role")}
                    className={inputCls}
                  >
                    {roles.map((r) => (
                      <option key={r.key} value={r.key}>{r.name || r.label || r.key.replace("_", " ")}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Temp password" hint="Leave blank to generate one. You'll see it once so you can share it.">
                  <input
                    data-testid={TID.addUserPassword}
                    value={form.password}
                    onChange={set("password")}
                    placeholder="Auto-generate"
                    className={inputCls + " font-mono"}
                  />
                </Field>
              </>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Telegram binding (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Telegram ID">
                <input
                  data-testid={TID.addUserTelegramId}
                  value={form.telegram_id}
                  onChange={(e) => setForm((f) => ({ ...f, telegram_id: e.target.value.replace(/[^0-9]/g, "") }))}
                  inputMode="numeric"
                  placeholder="5973710183"
                  className={inputCls + " font-mono"}
                />
              </Field>
              <Field label="Username">
                <input
                  data-testid={TID.addUserTelegramUsername}
                  value={form.telegram_username}
                  onChange={(e) => setForm((f) => ({ ...f, telegram_username: e.target.value.replace(/^@/, "") }))}
                  placeholder="@handle"
                  className={inputCls}
                />
              </Field>
            </div>
            <p className="text-[10px] text-slate-500">
              @username-only is fine — the numeric ID resolves on the user's first <code>/start</code>.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-3">
          <button
            data-testid={TID.addUserCancel}
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid={TID.addUserSubmit}
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-60"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
            Add user
          </button>
        </div>
      </form>
    </div>
  );
};

// ---------- Delete confirm ----------
const ConfirmDelete = ({ user, onClose, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await usersApi.delete(user.user_id);
      toast.success(`${user.display_name} deleted`);
      onDeleted();
    } catch (e) {
      toast.error(getApiError(e, "Delete failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-md border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="font-heading text-base font-bold text-slate-900">Delete user</h3>
        </div>
        <div className="p-5 text-sm text-slate-800">
          Permanently delete <b>{user.display_name}</b>?
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-900">
            Immediately revokes their web login and Telegram bot access. This cannot be undone.
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button onClick={onClose} disabled={busy} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button
            data-testid={`confirm-delete-user-${user.user_id}`}
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================================================
// Main page
// ==========================================================================
export default function Users() {
  const me = auth.me();
  const [users, setUsers] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null); // { permissions, presets } for TG perms
  const [webTaxonomy, setWebTaxonomy] = useState(null); // { roles } for role dropdown
  const [statusFilter, setStatusFilter] = useState("APPROVED");
  const [surfaceFilter, setSurfaceFilter] = useState("all"); // all | web | telegram | both
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [merging, setMerging] = useState(null);
  const [editingPerms, setEditingPerms] = useState(null);
  const [managingRoles, setManagingRoles] = useState(false);
  const [oneShot, setOneShot] = useState(null);

  const isSuperAdmin = me?.role === "SUPER_ADMIN";

  useEffect(() => {
    (async () => {
      try { setTaxonomy(await usersApi.telegramPermissionsTaxonomy()); }
      catch (e) { console.error("tg taxonomy load failed", e); }
      try { setWebTaxonomy(await webUsersApi.taxonomy()); }
      catch (e) { console.error("web taxonomy load failed", e); }
    })();
  }, []);

  const load = async () => {
    try {
      const rows = await usersApi.list(statusFilter === "ALL" ? undefined : statusFilter);
      // Annotate is_owner locally (backend only sets it on /web-users).
      const ownerEmail = null;  // preview: not exposed to client; fine to leave null.
      setUsers((rows || []).map((r) => ({ ...r, is_owner: ownerEmail && r.email === ownerEmail })));
    } catch (e) {
      toast.error(getApiError(e, "Failed to load users"));
      setUsers([]);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!users) return [];
    const needle = search.trim().toLowerCase();
    return users.filter((u) => {
      // Surface filter
      const hasWeb = !!u.email;
      const hasTg = !!(u.telegram_id || u.telegram_username);
      if (surfaceFilter === "web" && !hasWeb) return false;
      if (surfaceFilter === "telegram" && !hasTg) return false;
      if (surfaceFilter === "both" && !(hasWeb && hasTg)) return false;
      // Text search
      if (!needle) return true;
      return (
        (u.display_name || "").toLowerCase().includes(needle) ||
        (u.email || "").toLowerCase().includes(needle) ||
        String(u.telegram_id || "").includes(needle) ||
        (u.telegram_username || "").toLowerCase().includes(needle)
      );
    });
  }, [users, search, surfaceFilter]);

  const doReset = async (u) => {
    if (!u.email) return;
    if (!window.confirm(`Reset ${u.display_name}'s password? You'll see the new password once.`)) return;
    try {
      const res = await webUsersApi.resetPassword(u.user_id);
      setOneShot({
        email: u.email,
        password: res.new_password,
        note: `${u.display_name} will be forced to change this on next sign-in.`,
      });
    } catch (e) {
      toast.error(getApiError(e, "Reset failed"));
    }
  };

  return (
    <AppLayout mobileTitle="Users">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Access control</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            One row per person. Expand any user to view and edit their <b>web login</b> and <b>Telegram binding</b> in one place — including role, permissions, and password reset.
          </p>
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap">
          {isSuperAdmin && (
            <button
              data-testid={TID.manageRolesBtn}
              onClick={() => setManagingRoles(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-nmi-600 hover:text-nmi-700"
            >
              <Shield size={13} /> Manage roles
            </button>
          )}
          <button
            data-testid={TID.addUserButton}
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-2 text-xs font-semibold text-white hover:bg-nmi-800"
          >
            <UserPlus size={14} strokeWidth={2.5} /> Add user
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {["APPROVED", "PENDING", "REVOKED", "ALL"].map((s) => (
          <button
            key={s}
            data-testid={TID.usersStatusFilter(s.toLowerCase())}
            onClick={() => setStatusFilter(s)}
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
              statusFilter === s
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {s}
          </button>
        ))}

        <div className="mx-2 h-6 w-px bg-slate-200" />

        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
          {[
            { key: "all", label: "All surfaces" },
            { key: "web", label: "Web only" },
            { key: "telegram", label: "Telegram only" },
            { key: "both", label: "Both" },
          ].map(({ key, label }) => (
            <button
              key={key}
              data-testid={TID.usersSurfaceFilter(key)}
              onClick={() => setSurfaceFilter(key)}
              className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                surfaceFilter === key ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="relative ml-auto min-w-0">
          <Search size={12} className="absolute left-2.5 top-2.5 text-slate-400" strokeWidth={2.5} />
          <input
            data-testid={TID.usersSearch}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, TG…"
            className="w-56 rounded-md border border-slate-200 bg-white pl-7 pr-3 py-1.5 text-xs outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
          />
        </div>

        <div className="text-[11px] font-medium text-slate-500 whitespace-nowrap">
          {users === null ? "Loading…" : `${filtered.length} user${filtered.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="w-8 px-2 py-3" />
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">User</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Contact</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Role</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Surfaces</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {users === null && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500"><Loader2 size={18} className="inline animate-spin" /></td></tr>
            )}
            {users !== null && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="p-10 text-center text-slate-500">
                  {users.length === 0
                    ? <>No users. Click <b>Add user</b> to provision your first bound user.</>
                    : <>No users match this filter.</>}
                </td>
              </tr>
            )}
            {filtered.map((u) => (
              <React.Fragment key={u.user_id}>
                <tr
                  data-testid={TID.userRow(u.user_id)}
                  onClick={() => setExpanded(expanded === u.user_id ? null : u.user_id)}
                  className={`cursor-pointer border-b border-slate-200 last:border-b-0 transition-colors ${
                    expanded === u.user_id ? "bg-nmi-50/50" : "hover:bg-slate-50"
                  }`}
                >
                  <td className="px-2 py-3">
                    <button
                      data-testid={TID.userExpandToggle(u.user_id)}
                      className="rounded p-1 text-slate-400 hover:text-nmi-700"
                      aria-label={expanded === u.user_id ? "Collapse" : "Expand"}
                    >
                      {expanded === u.user_id
                        ? <ChevronDown size={16} strokeWidth={2.5} />
                        : <ChevronRight size={16} strokeWidth={2.5} />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {u.avatar_url
                        ? <img src={u.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                        : <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">{(u.display_name || "?").slice(0, 2).toUpperCase()}</div>}
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{u.display_name}</div>
                        <div className="text-[10px] font-mono text-slate-400 truncate max-w-[180px]">{u.user_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-slate-700">
                      {u.email || <span className="italic text-slate-400">no email</span>}
                    </div>
                    {(u.telegram_id || u.telegram_username) && (
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        {u.telegram_username && <span>@{u.telegram_username}</span>}
                        {u.telegram_username && u.telegram_id && <span className="mx-1">·</span>}
                        {u.telegram_id && <span className="font-mono">{u.telegram_id}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-4 py-3"><SurfaceChips user={u} /></td>
                  <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                </tr>
                {expanded === u.user_id && (
                  <tr>
                    <td colSpan={6} className="p-0">
                      <UserExpandedPanel
                        user={u}
                        taxonomy={webTaxonomy}
                        me={me}
                        onUpdated={load}
                        onOpenTgPerms={() => setEditingPerms(u)}
                        onResetPassword={() => doReset(u)}
                        onDelete={() => setConfirmDelete(u)}
                        onMerge={() => setMerging(u)}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Bound-only access</div>
        Only pre-provisioned users can use the Telegram bot. Anyone else messaging the bot receives an <b>Access denied</b> response with their Telegram ID so you can add them here.
      </div>

      {/* Modals */}
      {showAdd && (
        <AddUserModal
          roles={webTaxonomy?.roles || [{ key: "VIEWER" }, { key: "ADMIN" }, { key: "SUPER_ADMIN" }]}
          onClose={() => setShowAdd(false)}
          onCreated={({ user, initial_password }) => {
            setShowAdd(false);
            load();
            if (initial_password) {
              setOneShot({
                email: user.email,
                password: initial_password,
                note: "User will be forced to change this on their first sign-in.",
              });
            } else {
              toast.success(`${user.display_name} added`);
            }
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmDelete
          user={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onDeleted={() => { setConfirmDelete(null); load(); }}
        />
      )}
      {merging && (
        <MergeUserModal
          source={merging}
          onClose={() => setMerging(null)}
          onMerged={() => { setMerging(null); load(); }}
        />
      )}
      {editingPerms && taxonomy && (
        <EditTelegramPermissionsModal
          user={editingPerms}
          taxonomy={taxonomy}
          onClose={() => setEditingPerms(null)}
          onSaved={() => { setEditingPerms(null); load(); }}
        />
      )}
      {managingRoles && <RolesManagerModal onClose={() => setManagingRoles(false)} />}
      {oneShot && <OneShotPasswordModal {...oneShot} onClose={() => setOneShot(null)} />}
    </AppLayout>
  );
}

// ---------- tiny local UI helpers ----------
const inputCls =
  "w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20";

const Field = ({ label, hint, required, children }) => (
  <div>
    <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    {children}
    {hint && <div className="mt-1 text-[10px] text-slate-500">{hint}</div>}
  </div>
);
